/**
 * AssetRadar — display formatting helpers.
 *
 * The one rule that matters here: NEVER call `new Date(x).toLocaleString()`
 * directly on a server value. Section 5 of lib/types.ts is normalised ISO-8601
 * (`IsoDateTime`), but sections 6/7 pass CMDBuild values through untouched
 * (`CmdbDateTime`) — those can be '31/12/2026 08:15:00', 'yyyy-MM-dd' or epoch
 * millis, and `new Date()` produces `Invalid Date` for the first form.
 * `fmtDate` reproduces the fallback chain of the old vanilla dashboard.
 */

import type { CmdbDateTime, IsoDateTime, Tone } from './types';

/** Em dash used for every "no value" cell, matching the old pages. */
export const DASH = '—';

const DE_DATETIME: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' };
const DE_DATE: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };

/**
 * Format an ISO-8601 or raw CMDBuild timestamp for German display.
 * Unparsable values degrade to their first 16 characters with the 'T' removed;
 * null/undefined/'' render as an em dash.
 */
export function fmtDate(value: CmdbDateTime | IsoDateTime | null | undefined): string {
  if (value === null || value === undefined || value === '') return DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 16).replace('T', ' ');
  }
  return date.toLocaleString('de-DE', DE_DATETIME);
}

/** Date-only variant for KEV dates ('YYYY-MM-DD'). */
export function fmtDay(value: CmdbDateTime | IsoDateTime | null | undefined): string {
  if (value === null || value === undefined || value === '') return DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString('de-DE', DE_DATE);
}

/** Render any nullable scalar, falling back to an em dash. */
export function fmtValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  const text = String(value).trim();
  return text === '' ? DASH : text;
}

/** Keep legacy xDome demo identifiers out of version labels; preserve real versions. */
export function fmtVersion(value: string | null | undefined, productName?: string): string {
  const version = value?.trim() ?? '';
  if (!version || version === 'demo-unknown') return 'Nicht angegeben';
  if (productName && version === `${productName} Demo`) return 'Demo';
  const prefix = productName ? `${productName} v` : '';
  const demoVersion = prefix && version.startsWith(prefix) ? version.slice(prefix.length) : version;
  if (demoVersion === 'Demo' || /^demo-[a-f0-9]{12}$/.test(demoVersion)) return 'Demo';
  return version;
}

/**
 * CVSS base scores arrive as a number OR as a string (the CSV import writes
 * CMDBuild "double" attributes as text). Normalise for display only.
 */
export function fmtScore(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return DASH;
  const num = typeof value === 'number' ? value : Number.parseFloat(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** German integer grouping. */
export function fmtNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return value.toLocaleString('de-DE');
}

/** Correct German singular/plural, e.g. `plural(1, 'Änderung', 'Änderungen')`. */
export function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

/** `1 Änderung` / `3 Änderungen`. */
export function countLabel(count: number, singular: string, pluralForm: string): string {
  return `${count} ${plural(count, singular, pluralForm)}`;
}

/** Shorten free text for a table cell, keeping the full value for a title attribute. */
export function truncate(value: string | null | undefined, max = 60): string {
  if (!value) return '';
  const text = String(value);
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** German typographic quotes around a (possibly truncated) quotation. */
export function quote(value: string): string {
  return `„${value}“`;
}

/** Tone for a counter: zero is good, anything above zero is the given tone. */
export function toneForCount(count: number, nonZero: Exclude<Tone, 'neutral'> = 'alert'): Tone {
  return count > 0 ? nonZero : 'good';
}

/** Distinct, order-preserving list of non-empty strings. */
export function uniqueStrings(values: readonly (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    if (!value) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}
